import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Search, Filter, RefreshCw } from 'lucide-react';
import { FinancialEventsResponse, FinancialEventType, FinancialEventStatus } from '@/types/bi';
import biApi from '@/services/biApi';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { toast } from '@/components/ui/use-toast';
import * as XLSX from 'xlsx';

interface FinancialEventsTableProps {
  clientId?: number;
}

const FinancialEventsTable: React.FC<FinancialEventsTableProps> = ({ clientId }) => {
  const [data, setData] = useState<FinancialEventsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    interactionId: '',
    eventType: '' as FinancialEventType | '',
    status: '' as FinancialEventStatus | '',
    startDate: '',
    endDate: '',
  });

  const loadData = async () => {
    try {
      setIsLoading(true);
      const response = await biApi.getFinancialEvents({
        clientId,
        interactionId: filters.interactionId || undefined,
        eventType: filters.eventType || undefined,
        status: filters.status || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        page,
        limit: 50,
      });
      setData(response);
    } catch (error) {
      console.error('Erro ao carregar eventos financeiros:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os eventos financeiros',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Só carregar se tiver clientId ou se não for admin
    if (clientId !== undefined) {
      loadData();
    }
  }, [clientId, page, filters]);

  const handleExport = () => {
    if (!data?.data) return;

    const worksheet = XLSX.utils.json_to_sheet(
      data.data.map((event) => ({
        ID: event.id,
        'ID da Interação': event.interaction_id || 'N/A',
        'ID da Fatura': event.invoice_id,
        'Tipo de Evento': event.event_type,
        'Valor Gerado': event.amount_generated,
        'Valor Pago': event.amount_paid,
        'Data de Vencimento': event.due_date || 'N/A',
        'Data de Pagamento': event.payment_date ? format(new Date(event.payment_date), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A',
        'Método de Pagamento': event.payment_method || 'N/A',
        Status: event.status,
        'Data de Criação': format(new Date(event.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }),
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Eventos Financeiros');
    XLSX.writeFile(workbook, `eventos-financeiros-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);

    toast({
      title: 'Sucesso',
      description: 'Arquivo exportado com sucesso',
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusBadge = (status: FinancialEventStatus) => {
    const variants: Record<FinancialEventStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      PAGO: 'default',
      PENDENTE: 'secondary',
      CANCELADO: 'outline',
      VENCIDO: 'destructive',
    };
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  if (!clientId) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-gray-500">Selecione um cliente para visualizar os eventos financeiros</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading && !data) {
    return <AtomicLoadingScreen />;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Eventos Financeiros</CardTitle>
            <CardDescription>Histórico completo de eventos financeiros</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
            <Button onClick={loadData} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Input
            placeholder="ID da Interação"
            value={filters.interactionId}
            onChange={(e) => setFilters({ ...filters, interactionId: e.target.value })}
          />
          <Select
            value={filters.eventType || 'all'}
            onValueChange={(value) => setFilters({ ...filters, eventType: value === 'all' ? '' as FinancialEventType : value as FinancialEventType })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value={FinancialEventType.EMISSAO_2VIA}>Emissão 2ª Via</SelectItem>
              <SelectItem value={FinancialEventType.NEGOCIACAO}>Negociação</SelectItem>
              <SelectItem value={FinancialEventType.ALERTA_SUSPENSAO}>Alerta Suspensão</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' as FinancialEventStatus : value as FinancialEventStatus })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value={FinancialEventStatus.PAGO}>Pago</SelectItem>
              <SelectItem value={FinancialEventStatus.PENDENTE}>Pendente</SelectItem>
              <SelectItem value={FinancialEventStatus.CANCELADO}>Cancelado</SelectItem>
              <SelectItem value={FinancialEventStatus.VENCIDO}>Vencido</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="date"
            placeholder="Data Início"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
          <Input
            type="date"
            placeholder="Data Fim"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
          <Button onClick={() => setFilters({
            interactionId: '',
            eventType: '' as FinancialEventType | '',
            status: '' as FinancialEventStatus | '',
            startDate: '',
            endDate: '',
          })} variant="outline">
            Limpar
          </Button>
        </div>

        {/* Tabela */}
        {isLoading ? (
          <div className="text-center py-8">Carregando...</div>
        ) : !data || data.data.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Nenhum evento encontrado</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Interação</TableHead>
                    <TableHead>Fatura</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Valor Gerado</TableHead>
                    <TableHead className="text-right">Valor Pago</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead>Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="font-mono text-xs">{event.id.substring(0, 8)}...</TableCell>
                      <TableCell>
                        {event.interaction ? (
                          <div className="text-sm">
                            <div className="font-medium">{event.interaction.customer_name}</div>
                            <div className="text-xs text-gray-500">{event.interaction.customer_phone}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{event.invoice_id}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{event.event_type.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(event.amount_generated)}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">
                        {formatCurrency(event.amount_paid)}
                      </TableCell>
                      <TableCell>{getStatusBadge(event.status)}</TableCell>
                      <TableCell>
                        {event.payment_date ? (
                          <div className="text-sm">
                            <div>{format(new Date(event.payment_date), 'dd/MM/yyyy', { locale: ptBR })}</div>
                            <div className="text-xs text-gray-500">{event.payment_method}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(event.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Paginação */}
            {data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-500">
                  Página {data.pagination.page} de {data.pagination.totalPages} ({data.pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    variant="outline"
                    size="sm"
                  >
                    Anterior
                  </Button>
                  <Button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FinancialEventsTable;

